<script setup lang="ts">
const siteUrl = useSiteUrl()

import { ChartNoAxesCombined, CircleCheckBig, MessageCircleQuestionMark, Route } from 'lucide-vue-next'
import type { Component } from 'vue'
import heroImage from '@/assets/img/about_us_hero.png'

definePageMeta({
  layout: 'default',
})
const pageTitle = 'درباره پی‌جیب'
const pageDescription =
  'پی‌جیب بیش از ۸ سال است در حوزه‌ی انتقال ارز و پرداخت‌های بین‌المللی فعالیت می‌کند و پلی امن میان کاربران ایران و اروپا ساخته است. با پی‌جیب، هر پرداخت بین‌المللی به سادگی یک پرداخت داخلی است.'
const canonicalUrl = siteUrl('/about')
useSeoMeta({
  title: `${pageTitle} | پی‌جیب`,
  description: pageDescription,
  ogTitle: `${pageTitle} | پی‌جیب`,
  ogDescription: pageDescription,
  ogType: 'website',
  ogUrl: canonicalUrl,
  ogLocale: 'fa_IR',
  twitterCard: 'summary_large_image',
  twitterTitle: `${pageTitle} | پی‌جیب`,
  twitterDescription: pageDescription,
  robots: 'index, follow',
})
useHead({
  htmlAttrs: {
    lang: 'fa',
  },

  link: [
    {
      rel: 'canonical',
      href: canonicalUrl,
    },
  ],

  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: pageTitle,
        description: pageDescription,
        url: canonicalUrl,
        mainEntity: {
          '@type': 'Organization',
          name: 'پی‌جیب',
          url: siteUrl('/'),
        },
      }),
    },
  ],
})
type StorySection = {
  title: string
  paragraphs: string[]
  points: string[]
}

type ValueCard = {
  title: string
  description: string
  icon: Component
  accent: 'primary' | 'secondary'
}
const storySections: StorySection[] = [
  {
    title: 'تیم پشتیبانی حرفه‌ای در کنار شما تا آخرین قدم',
    paragraphs: [
      'تیم پشتیبانی پی‌جیب فقط یک ساختار سازمانی پاسخ‌دهنده نیست؛ بلکه تدبیر ما همراهی واقعی شما در تمام مسیر انتقال است. از اولین لحظه‌ای که درخواستتان را ثبت می‌کنید تا زمانی که وجه به دست گیرنده برسد، یک پشتیبان اختصاصی در کنار شما خواهد بود.',
      'از طرفی تمام پیگیری درخواست‌ها به صورت شبانه‌روزی انجام می‌شود، بدون تعطیلی و بدون تأخیر. با پی‌جیب دقت بالا در دریافت و تأیید مدارک هر دو طرف انتقال، احتمال خطا و برگشت تراکنش به حداقل ممکن می‌رسد. اگر در هر مرحله سؤال یا چالشی ذهنتان را درگیر کرد، تیم ما آماده‌ی راهنمایی است تا تجربه‌ای بدون استرس داشته باشید.',
    ],
    points: ['پشتیبان اختصاصی', 'پیگیری شبانه‌روزی', 'دریافت و تأیید مدارک'],
  },
  {
    title: 'تجربه مطمئن، در مقیاس جهانی',
    paragraphs: [
      'پشت هر تراکنش موفق تجربه‌ای ارزشمند نهفته است. پی‌جیب تاکنون ده‌ها هزار تراکنش موفق را با نرخ‌های بالا به انجام رسانده و تجربه پرداخت‌هایی در بیش از ۱۰ کشور جهان داشته است.',
      'این اعتماد فقط با سرعت ساخته نمی‌شود؛ با نشان دادن کیفیت، پایداری و گستردگی واقعی خدمات ما ساخته می‌شود. هر چالشی که در مسیر انتقال پیش بیاید، از تفاوت‌های بانکی گرفته تا مقررات ارزی کشورها، به تکیه بر همین تجربه در کنار شما می‌مانیم و راه حل مناسب را پیدا می‌کنیم.',
    ],
    points: ['ده‌ها هزار تراکنش موفق', 'بیش از ۱۰ کشور', 'راهکار مناسب'],
  },
  {
    title: 'پلتفرمی امن و آسان برای پرداخت‌های بین‌المللی',
    paragraphs: [
      'سایت و پنل کاربری پی‌جیب با تمرکز بر پایداری و امنیت طراحی شده‌اند تا محیطی مطمئن برای تراکنش‌های شما فراهم آورد. از پرداخت‌های بین‌المللی، خرید اینترنت، ویزا کارت اپلیکیشن‌ها و پلتفرم‌های هوش مصنوعی، همه در یک بستر یکپارچه و ساده انجام می‌شوند.',
      'که نتیجه آن برای شما، تجربه‌ای سریع و مطمئن از خدمات ارزی است، بدون پیچیدگی‌های رایج. هدف ما این است که هر پرداخت بین‌المللی به سادگی یک پرداخت داخلی باشد.',
    ],
    points: ['پایداری و امنیت', 'بستر یکپارچه و ساده', 'پرداخت بین‌المللی'],
  },
]

const valueCards: ValueCard[] = [
  {
    title: 'زیر ساخت به‌روز',
    description: 'از فناوری‌های روز استفاده می‌کنیم تا تراکنش‌ها سریع و پایدار بمانند.',
    icon: ChartNoAxesCombined,
    accent: 'primary',
  },
  {
    title: 'تیم پشتیبانی پاسخگو',
    description: 'تیم ما فقط پاسخ نمی دهد بلکه شما را همراهی می کند.',
    icon: MessageCircleQuestionMark,
    accent: 'secondary',
  },
  {
    title: 'فرآیند قابل پیگیری',
    description: 'از لحظه ارسال تا دریافت، همه‌چیز شفاف و قابل رصد است.',
    icon: Route,
    accent: 'primary',
  },
  {
    title: 'هزینه‌ی منصفانه',
    description: 'تجربه‌ای به‌صرفه، بدون هزینه‌های پنهان .',
    icon: CircleCheckBig,
    accent: 'secondary',
  },
]
</script>

<template>
  <main class="min-h-screen bg-white text-ink pt-28">
    <section class="px-5 py-10 sm:py-12">
      <div class="mx-auto flex w-full max-w-5xl flex-col items-center gap-8 lg:flex-row-reverse lg:justify-between">
        <div class="order-2 w-full max-w-[444px] rounded-xl bg-[#EEF0FF] p-3 opacity-80 sm:p-4 lg:order-1">
          <img :height="249" :width="444" alt="hero_about_us" :src="heroImage" class="h-auto w-full" />
        </div>
        <div class="order-1 w-full text-right lg:order-2 lg:flex-1 lg:border-r-4 lg:border-primary lg:pr-5">
          <div class="relative inline-block">
            <span class="absolute -top-3 right-0 size-2.5 rounded-full bg-secondary sm:-top-4 sm:size-3" />
            <h1 class="text-3xl font-black leading-tight text-ink sm:text-4xl lg:text-5xl">
              درباره پی‌جیب
            </h1>
            <span class="mt-4 mr-0 block h-1 w-[55%] rounded-full bg-primary sm:mt-5" />
          </div>
          <div
            class="mt-6 flex max-w-2xl flex-col gap-4 text-sm leading-7 text-muted sm:mt-8 sm:text-base sm:leading-8">
            <p>
              در دنیایی که هر تراکنش مهم است، انتقال و جابه‌جایی ارز باید باعث
              سردرگمی، انتظار و نگرانی شما شود. هدف ما این است که در پی‌جیب این
              مسیر را از ابتدا شفاف، سریع، ساده و قابل اعتماد کنیم.
            </p>
            <p>
              تیم پی‌جیب بیش از ۸ سال است که در حوزه‌ی انتقال ارز و پرداخت‌های
              بین‌المللی فعالیت دارد و در این مدت، پلی امن ساخته است میان کاربران
              ایران و اروپا؛ پلی که هزاران کاربر را داخل و خارج کشور، برای فرستادن
              و دریافت وجه به آن اعتماد کرده‌اند.
            </p>
          </div>
        </div>
      </div>
    </section>
    <section class="bg-white px-5 pb-12 pt-16 text-right sm:px-8 sm:pt-20">
      <div class="mx-auto max-w-5xl">
        <h2 class="text-2xl font-black text-ink sm:text-3xl">
          چرا پی‌جیب یک انتخاب هوشمندانه است؟
        </h2>

        <p class="mt-4 text-muted">
          دلیلش توجه و احترام ما به نیازهای اصلی کاربران است :
        </p>

        <div class="mt-8 h-px bg-border -mx-4" />
      </div>
    </section>
    <section v-for="(story, index) in storySections" :key="story.title" :class="[
      'py-16 sm:py-20',
      index === 1 ? 'bg-surface' : 'bg-white',
    ]">
      <div
        class="mx-auto grid max-w-5xl gap-10 px-5 sm:px-8 sm:pt-20 sm:pb-20 lg:grid-cols-[1fr_15rem] lg:items-start lg:gap-16">

        <!-- Content -->
        <div class="order-1 text-right lg:order-1">
          <h2 class="text-2xl font-black leading-relaxed text-ink sm:text-3xl">
            {{ story.title }}
          </h2>

          <div class="mt-6 flex flex-col gap-5 text-base leading-8 text-muted">
            <p v-for="paragraph in story.paragraphs" :key="paragraph">
              {{ paragraph }}
            </p>
          </div>
        </div>
        <aside :class="[
          'order-2 lg:order-2 relative overflow-hidden rounded-xl border border-border p-5 text-right',
          {
            'bg-surface-strong': index % 2 === 0,
            'bg-white': index % 2 !== 0,
          },
        ]">
          <span class="absolute inset-y-0 right-0 w-1 rounded-full bg-primary" />

          <p class="mb-4 border-b border-border pb-3 text-sm font-bold text-primary">
            خلاصه بخش
          </p>

          <ul class="flex flex-col gap-4 text-sm leading-5 text-ink font-bold">
            <li v-for="point in story.points" :key="point" class="flex items-center gap-3">
              <span class="size-1.5 shrink-0 rounded-full bg-primary" />
              <span>{{ point }}</span>
            </li>
          </ul>
        </aside>
      </div>
    </section>
    <section class="bg-surface px-5 py-16 sm:px-8 sm:py-20">
      <div class="mx-auto max-w-5xl">
        <h2 class="text-right text-2xl font-black text-ink sm:text-3xl">
          دلیل تفاوت پی‌جیب چیست؟
        </h2>
        <div class="mt-8 grid gap-4 sm:grid-cols-2">
          <article v-for="item in valueCards" :key="item.title"
            class="rounded-xl border border-border bg-white p-6 text-right shadow-[0_2px_8px_rgba(40,43,88,0.02)]">
            <div class="mb-5 flex items-center justify-start gap-4">
              <span :class="[
                'flex size-9 items-center justify-center rounded-lg text-primary',
                item.accent === 'primary'
                  ? 'bg-primary-soft '
                  : 'bg-secondary-soft',
              ]">
                <component :is="item.icon" class="size-5" />
              </span>

              <h3 class="font-bold text-ink">
                {{ item.title }}
              </h3>
            </div>

            <p class="text-sm leading-7 text-muted">
              {{ item.description }}
            </p>
          </article>
        </div>
      </div>
    </section>
    <section class="bg-white px-5 py-16 sm:px-8 sm:py-24">
      <div class="relative mx-auto max-w-5xl py-2 pr-8 text-right sm:pr-10">
        <span class="absolute inset-y-0 right-0 w-1 rounded-full bg-primary" />

        <p class="text-base leading-9 text-muted sm:text-lg sm:leading-10">
          ما باور داریم دسترسی به خدمات مالی، حق طبیعی هر فرد است؛ نه امتیازی محدود به مرزها یا
          <br />
          پیچیدگی‌های ساختارهای سنتی. به همین دلیل هر روز قدمی برمی‌داریم تا این تجربه ساده‌تر،
          <br />
          روان‌تر و انسانی‌تر شود.
        </p>

        <h2 class="mt-8 text-2xl font-black text-primary sm:text-3xl">
          <span class="inline-block border-b-4 border-secondary pb-2">
            شفافیت، ارزش
          </span>
          ماست.
        </h2>
      </div>
    </section>
  </main>
</template>
